import React, { useCallback, useMemo } from 'react';
import { connect, ErrorMessage, FormikContextType } from 'formik';
import styled from 'styled-components';
import { observer } from 'mobx-react';

import { Combobox, Switcher } from '@shared/fields';

import DatePickerField from '@shared/fields/DatePickerField';
import { LabeledField } from '../../LabeledField';
import { StarRating } from '../../StarRating';
import { TextareaHints } from '../TextareaHints';
import Textareafield from '../../fields/Textareafield';
import Field from '../../fields/Field';
import { Checkbox } from '../../fields/Checkbox';
import { CheckboxLabel } from '../CheckboxLabel';
import Btn from '../../buttons/Btn';
import { isMobile, REVIEW_MIN_DATE_ISO } from '../../../constants';
import { Footnote } from './Footnote';
import { ERROR_OCCURED, getFormTexts } from './formDictionary';
import {
  BLUE_GREY, ERROR_COLOR, FONT_FAMILY_MAIN, TEXT_COLOR,
} from '../../../theme';
import media from '../../../utils/mediaQueries';
import { withScrollToError } from '../../../hocs/withScrollToError';
import stores from '../../../stores';
import { UserSchool } from './types/UserSchool';
import { ReviewerOption } from './types/ReviewerOption';
import { StudentFormValues } from './types/StudentFormValues';

const FieldComment = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  font-size: 12px;
  line-height: 1.17;
  letter-spacing: -0.1px;
  color: ${BLUE_GREY};
  margin-top: 8px;
`;

const ErrorMessageContainer = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  color: ${ERROR_COLOR};
  font-size: 12px;
  line-height: 1.17;
  margin-top: 3px;
`;

const ContactInfoNote = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  font-size: 18px;
  line-height: 1.33;
  letter-spacing: 0.2px;
  color: ${TEXT_COLOR};
  max-width: 616px;
  margin: 62px 0 16px;

  ${media.mobile`
    margin-top: 48px;
    max-width: none;
  `}
`;

const GroupedField = styled.div`
  ${(props) => props.hidden && 'display: none;'}

  & + & {
    margin-top: 16px;
  }
`;

const ContactCaption = styled.div`
  color: ${BLUE_GREY};
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.17;
  letter-spacing: -0.1px;
  font-weight: 400;
`;

type FormProps = {
  userSchools?: UserSchool[];
  reviewTextRef?: React.RefObject<HTMLTextAreaElement>;
  setReviewerType?(type: string): void;
  reviewersOptions?: ReviewerOption[];
  minCommentLength?: number;
};

type FormikProps = {
  formik: FormikContextType<StudentFormValues>;
};

const FormComponent = (props: FormProps & FormikProps) => {
  const {
    starsData, textarea, contactInfoNote, receptionData,
    informationData, teachingData, availabilityData, qualityData,
    licenseType, drivingLessonsAndFullCost, theoryAndDrivivngExams,
    contact, userSchoolsData, practicalExamDate,
  } = useMemo(() => getFormTexts(''), []);
  const {
    formik, userSchools, reviewTextRef, setReviewerType, reviewersOptions, minCommentLength,
  } = props;
  const {
    values, errors, touched, handleChange, handleBlur, isSubmitting, handleSubmit, status, setFieldValue,
  } = formik;
  const { meetingSubjects } = stores.meetingStore;

  const handleSchoolChange = (e: React.ChangeEvent<HTMLSelectElement>, setValue: (field: string, value: string) => void) => {
    const { meetingStore, schoolReviewStore } = stores;
    meetingStore.getMeetingSubjects(e.currentTarget.value);
    const selectedSchool = userSchools.find((school) => school.id === e.currentTarget.value);
    schoolReviewStore.setPlaceId(selectedSchool.placeId);
    setValue(e.currentTarget.name, e.currentTarget.value);
  };

  const handleReviewerTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>, setValue: (field: string, value: string) => void) => {
    setReviewerType(e.currentTarget.value);
    setValue(e.currentTarget.name, e.currentTarget.value);
  }, []);

  return (
    <form onSubmit={handleSubmit} data-test-selector="post-new-rating">
      {userSchools.length
        ? (
          <LabeledField label={userSchoolsData.label}>
            <Combobox
              name="selectedSchoolId"
              width="100%"
              options={userSchools}
              onChange={(e) => handleSchoolChange(e, setFieldValue)}
              onBlur={handleBlur}
              value={values.selectedSchoolId || userSchools[0].id}
            />
          </LabeledField>
        )
        : ''}
      <LabeledField label="Vous êtes" isRequired error={errors.reviewerType && touched.reviewerType}>
        <Combobox
          name="reviewerType"
          width="100%"
          options={reviewersOptions}
          onChange={(e) => handleReviewerTypeChange(e, setFieldValue)}
          onBlur={handleBlur}
          value={values.reviewerType}
          data-testid="reviewer-type"
        />
      </LabeledField>
      <LabeledField isRequired label={starsData.label} error={errors.stars && touched.stars}>
        <StarRating
          selectedStars={values.stars}
          onStarClick={(e) => {
            stores.schoolReviewStore.setRating(e.target.value);
            handleChange(e);
          }}
          name="stars"
        />
        <ErrorMessage component={ErrorMessageContainer} name="stars" />
      </LabeledField>
      <LabeledField label={receptionData.label}>
        <Switcher
          items={receptionData.items}
          name="reception"
          activeItem={values.reception}
          onChange={handleChange}
        />
        <FieldComment>{receptionData.caption}</FieldComment>
      </LabeledField>
      <LabeledField label={informationData.label}>
        <Switcher
          items={informationData.items}
          name="information"
          activeItem={values.information}
          onChange={handleChange}
        />
        <FieldComment>{informationData.caption}</FieldComment>
      </LabeledField>
      <LabeledField label={teachingData.label}>
        <Switcher
          items={teachingData.items}
          name="teaching"
          activeItem={values.teaching}
          onChange={handleChange}
        />
        <FieldComment>{teachingData.caption}</FieldComment>
      </LabeledField>
      <LabeledField label={availabilityData.label}>
        <Switcher
          items={availabilityData.items}
          name="availability"
          activeItem={values.availability}
          onChange={handleChange}
        />
        <FieldComment>{availabilityData.caption}</FieldComment>
      </LabeledField>
      <LabeledField label={qualityData.label}>
        <Switcher
          items={qualityData.items}
          name="quality"
          activeItem={values.quality}
          onChange={handleChange}
        />
        <FieldComment>{qualityData.caption}</FieldComment>
      </LabeledField>
      <LabeledField
        label={textarea.label}
        isRequired
        error={errors.textareaField && touched.textareaField}
        labelAddition={<TextareaHints value={values.textareaField} minLength={minCommentLength || 200} />}
      >
        <Textareafield
          name="textareaField"
          value={values.textareaField}
          onChange={handleChange}
          onBlur={handleBlur}
          valid={touched.textareaField && !errors.textareaField}
          textareaRef={reviewTextRef}
          resize="vertical"
        />
        <ErrorMessage component={ErrorMessageContainer} name="textareaField" />
        <FieldComment>
          <a href="/a-propos/conditions-particulieres-avis" target="_blank" rel="noopener noreferrer">
            {textarea.rulesLink}
          </a>
          <div>{textarea.comment}</div>
        </FieldComment>
      </LabeledField>
      <ContactInfoNote>{contactInfoNote}</ContactInfoNote>
      <LabeledField label={licenseType.label}>
        <Combobox
          placeholder={licenseType.placeholder}
          name="licenseType"
          width="100%"
          options={meetingSubjects}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.licenseType}
        />
        <ErrorMessage component={ErrorMessageContainer} name="licenseType" />
      </LabeledField>
      <LabeledField isRequired label={practicalExamDate.label}>
        <DatePickerField
          name="practicalExamDate"
          placeholderText={practicalExamDate.placeholder}
          value={values.practicalExamDate}
          maxDate={new Date()} // today
          minDate={new Date(REVIEW_MIN_DATE_ISO)}
          onChange={setFieldValue}
          onBlur={handleBlur}
          dateFormat="dd MMMM yyyy"
          valid={touched.practicalExamDate && !errors.practicalExamDate}
        />
        <ErrorMessage component={ErrorMessageContainer} name="practicalExamDate" />
      </LabeledField>
      <LabeledField label={drivingLessonsAndFullCost.label}>
        <GroupedField>
          <Field
            placeholder={drivingLessonsAndFullCost.drivingLessonsPlaceholder}
            name="drivingLessons"
            type="number"
            min={0}
            step="any"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.drivingLessons}
            valid={touched.drivingLessons && !errors.drivingLessons}
          />
          <ErrorMessage component={ErrorMessageContainer} name="drivingLessons" />
        </GroupedField>
        <GroupedField>
          <Field
            placeholder={drivingLessonsAndFullCost.fullCostPlaceholder}
            name="fullCost"
            type="number"
            min={0}
            step="any"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.fullCost}
            valid={touched.fullCost && !errors.fullCost}
          />
          <ErrorMessage component={ErrorMessageContainer} name="fullCost" />
        </GroupedField>
      </LabeledField>
      <LabeledField label={theoryAndDrivivngExams.label}>
        <GroupedField>
          <Field
            placeholder={theoryAndDrivivngExams.theoryExamsPlaceholder}
            name="theoryExams"
            type="number"
            min={0}
            step="any"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.theoryExams}
            valid={touched.theoryExams && !errors.theoryExams}
          />
          <ErrorMessage component={ErrorMessageContainer} name="theoryExams" />
        </GroupedField>
        <GroupedField>
          <Field
            placeholder={theoryAndDrivivngExams.drivingExamsPlaceholder}
            name="drivingExams"
            type="number"
            min={0}
            step="any"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.drivingExams}
            valid={touched.drivingExams && !errors.drivingExams}
          />
          <ErrorMessage component={ErrorMessageContainer} name="drivingExams" />
        </GroupedField>
      </LabeledField>
      <LabeledField
        label={contact.label}
        labelAddition={<ContactCaption>{contact.caption}</ContactCaption>}
        isRequired
        error={(errors.lastName && touched.lastName) || (errors.firstName && touched.firstName) || (errors.email && touched.email)}
      >
        <GroupedField>
          <Field
            placeholder={contact.lastNamePlaceholder}
            name="lastName"
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={touched.lastName && !errors.lastName}
          />
          <ErrorMessage component={ErrorMessageContainer} name="lastName" />
        </GroupedField>
        <GroupedField>
          <Field
            placeholder={contact.firstNamePlaceholder}
            name="firstName"
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={touched.firstName && !errors.firstName}
          />
          <ErrorMessage component={ErrorMessageContainer} name="firstName" />
        </GroupedField>
        <GroupedField>
          <Field
            placeholder={contact.emailPlaceholder}
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            valid={touched.email && !errors.email}
          />
          <ErrorMessage component={ErrorMessageContainer} name="email" />
        </GroupedField>
      </LabeledField>

      <LabeledField label="">
        <GroupedField>
          <Checkbox
            name="isAgreed"
            label={<CheckboxLabel />}
            isChecked={values.isAgreed}
            onChange={handleChange}
            error={errors.isAgreed && touched.isAgreed}
          />
          <ErrorMessage component={ErrorMessageContainer} name="isAgreed" />
        </GroupedField>
        <GroupedField hidden>
          <Checkbox
            name="allowMaifProposal"
            label="J'accepte que mes données personnelles soient transmises à MAIF pour recevoir des offres commerciales"
            isChecked={values.allowMaifProposal}
            onChange={handleChange}
            error={errors.allowMaifProposal && touched.allowMaifProposal}
          />
        </GroupedField>
      </LabeledField>
      <LabeledField label="">
        <Btn type="submit" text="Publier l'avis" isLoading={isSubmitting} disabled={isSubmitting} block={isMobile} name="submitReview" />
        {!!status && !status.success && <ErrorMessageContainer>{ERROR_OCCURED}</ErrorMessageContainer>}
        {errors.message && <ErrorMessageContainer>{errors.message}</ErrorMessageContainer>}
      </LabeledField>
      <LabeledField label="">
        <Footnote />
      </LabeledField>
    </form>
  );
};

FormComponent.defaultProps = {
  userSchools: [],
  reviewTextRef: null,
  setReviewerType: () => {},
  reviewersOptions: [],
  minCommentLength: 200,
};

const Form = connect(observer(FormComponent));

const StudentReviewFormComponent = withScrollToError(Form);

export { StudentReviewFormComponent };
